import React from 'react';
import { AlertTriangle, CheckCircle2, Info, Loader2 } from 'lucide-react';

import { Alert, AlertDescription, AlertTitle } from '~/shadcn/components/ui/Alert';
import { Button } from '~/shadcn/components/ui/Button';
import { BULK_UPLOAD_STATUS } from './useBulkUpload';

export function BulkUploadStatus({
    entityName,
    fetchError,
    handleStartNew,
    hasData,
    infoMessages = [],
    persistError,
    persistErrors = [],
    persistSuccess,
    status,
    uploadError,
    warningMessages = [],
}) {
    return (
        <>
            {/* Show error if upload failed */}
            {uploadError && (
                <Alert variant="destructive">
                    <AlertTitle>Upload failed</AlertTitle>
                    <AlertDescription>{uploadError.message}</AlertDescription>
                </Alert>
            )}

            {/* Show fetch error */}
            {fetchError && (
                <Alert variant="destructive">
                    <AlertTitle>Failed to load data</AlertTitle>
                    <AlertDescription>{fetchError.message}</AlertDescription>
                </Alert>
            )}

            {/* Loading state */}
            {status === BULK_UPLOAD_STATUS.LOADING && (
                <div className="flex items-center justify-center gap-2 py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span>Loading data...</span>
                </div>
            )}

            {/* Show editing spinner */}
            {hasData && status === BULK_UPLOAD_STATUS.SUBMITTING && (
                <div className="flex items-center justify-center gap-2 py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span>Saving changes...</span>
                </div>
            )}

            {/* Show persisting spinner */}
            {status === BULK_UPLOAD_STATUS.PERSISTING && (
                <div className="flex items-center justify-center gap-2 py-8">
                    <Loader2 className="h-6 w-6 animate-spin" />
                    <span>Creating {entityName}s...</span>
                </div>
            )}

            {/* Show persist error (mutation/network error) */}
            {persistError && (
                <Alert variant="destructive">
                    <AlertTitle>Failed to create {entityName}s</AlertTitle>
                    <AlertDescription>{persistError.message}</AlertDescription>
                </Alert>
            )}

            {/* Show row-level persist errors */}
            {persistErrors.length > 0 && (
                <Alert variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle>
                        {persistErrors.length} row{persistErrors.length !== 1 ? 's' : ''} failed to save
                    </AlertTitle>
                    <AlertDescription>
                        <ul className="mt-2 list-disc space-y-1 pl-4">
                            {persistErrors.map((error) => (
                                <li key={`persist-error-${error.row}`}>
                                    Row {error.row + 1}: {error.message}
                                </li>
                            ))}
                        </ul>
                    </AlertDescription>
                </Alert>
            )}

            {/* Show info messages */}
            {infoMessages.length > 0 &&
                infoMessages.map((message) => (
                    <Alert key={`info-${message}`} className="border-blue-500 text-blue-700">
                        <Info className="h-4 w-4" />
                        <AlertDescription>{message}</AlertDescription>
                    </Alert>
                ))}

            {/* Show warning messages */}
            {warningMessages.length > 0 &&
                warningMessages.map((message) => (
                    <Alert key={`warning-${message}`} className="border-yellow-500 text-yellow-700">
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>{message}</AlertDescription>
                    </Alert>
                ))}

            {/* Show success state */}
            {persistSuccess && (
                <div className="flex flex-col items-center justify-center gap-4 py-8">
                    <CheckCircle2 className="h-12 w-12 text-green-500" />
                    <div className="text-center">
                        <p className="text-lg font-medium">{entityName}s created successfully!</p>
                        <p className="text-sm text-muted-foreground">Your bulk upload has been processed.</p>
                    </div>
                    <Button onClick={handleStartNew}>Upload Another File</Button>
                </div>
            )}
        </>
    );
}
